import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Spinner } from "@/components/ui/spinner";
import { Download, RefreshCw, Clock } from "lucide-react";
import { DetailHeader } from "@/components/layout/detail-header";
import { Button } from "@/components/ui/button";
import { useToast } from "@/contexts/toast-context";

interface CycleMetrics {
  cycleName: string;
  timeInMapping: number;
  numberOfFilesMapped: number;
  timeInIdentityResolution: number;
  timeInValidations: number;
  totalTime: number;
  isComplete: boolean;
}

interface SpaceAnalysis {
  spaceId: string;
  spaceName: string;
  initialLoad: CycleMetrics | null;
  dualMaintenanceCycles: CycleMetrics[];
}

interface AverageMetrics {
  avgTimeInMapping: number;
  avgNumberOfFilesMapped: number;
  avgTimeInIdentityResolution: number;
  avgTimeInValidations: number;
  avgTotalTime: number;
}

interface JobsResponse {
  id: string;
  totalSpaces: number;
  averageMetrics: AverageMetrics;
  spaces: SpaceAnalysis[];
  createdAt: string;
}

export function JobsPage() {
  const { showError } = useToast();
  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState<JobsResponse | null>(null);
  const [searchValue, setSearchValue] = useState("");
  const [timeMode, setTimeMode] = useState<"real" | "work">("real");

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      if (!data) {
        setInitialLoading(true);
      }
      const response = await api.get("/jobs");
      setData(response);
    } catch (err) {
      console.error("Failed to fetch jobs:", err);
      const errorMessage = err instanceof Error ? err.message : "Failed to fetch jobs";
      showError("Failed to load job analysis", errorMessage);
    } finally {
      setInitialLoading(false);
    }
  };

  const refreshJobs = async () => {
    try {
      setRefreshing(true);
      const response = await api.post("/jobs/refresh", {});
      setData(response);
    } catch (err) {
      console.error("Failed to refresh jobs:", err);
      const errorMessage = err instanceof Error ? err.message : "Failed to refresh jobs";
      showError("Failed to refresh analysis", errorMessage);
    } finally {
      setRefreshing(false);
    }
  };

  // Convert real time to work hours (8h work day)
  const convertToWorkHours = (ms: number): number => {
    const hours = ms / (1000 * 60 * 60);
    
    // If less than or equal to 8 hours, no conversion needed
    if (hours <= 8) {
      return ms;
    }
    
    // Calculate days and convert to 8-hour work days
    const days = hours / 24;
    const workHours = days * 8;
    return workHours * 60 * 60 * 1000;
  };

  const formatDuration = (ms: number): string => {
    if (ms === 0) return "0s";
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes % 60}m ${seconds % 60}s`;
    }
    if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    }
    return `${seconds}s`;
  };

  // Apply time mode conversion (skip for mapping time)
  const getDisplayTime = (ms: number, isMapping = false): number => {
    if (isMapping || timeMode === "real") {
      return ms;
    }
    return convertToWorkHours(ms);
  };

  // Filter spaces based on search
  const filteredSpaces = data?.spaces.filter(space => {
    if (!searchValue.trim()) return true;
    const search = searchValue.toLowerCase();
    return (
      space.spaceName.toLowerCase().includes(search) ||
      space.spaceId.toLowerCase().includes(search)
    );
  }) || [];

  const exportToCSV = () => {
    if (!data) return;

    const rows: string[][] = [];
    
    // Header
    rows.push([
      "Space ID",
      "Space Name",
      "Cycle Type",
      "Time in Mapping",
      "Number of Files Mapped",
      "Time in Identity Resolution",
      "Time in Validations",
      "Total Time",
      "Time Mode"
    ]);

    // Data rows
    filteredSpaces.forEach(space => {
      if (space.initialLoad) {
        rows.push([
          space.spaceId,
          space.spaceName,
          space.initialLoad.cycleName,
          formatDuration(getDisplayTime(space.initialLoad.timeInMapping, true)),
          space.initialLoad.numberOfFilesMapped.toString(),
          formatDuration(getDisplayTime(space.initialLoad.timeInIdentityResolution)),
          formatDuration(getDisplayTime(space.initialLoad.timeInValidations)),
          formatDuration(getDisplayTime(space.initialLoad.totalTime)),
          timeMode === "real" ? "Real Time" : "Work Hours (8h/day)"
        ]);
      }

      space.dualMaintenanceCycles.forEach(cycle => {
        rows.push([
          space.spaceId,
          space.spaceName,
          cycle.cycleName,
          formatDuration(getDisplayTime(cycle.timeInMapping, true)),
          cycle.numberOfFilesMapped.toString(),
          formatDuration(getDisplayTime(cycle.timeInIdentityResolution)),
          formatDuration(getDisplayTime(cycle.timeInValidations)),
          formatDuration(getDisplayTime(cycle.totalTime)),
          timeMode === "real" ? "Real Time" : "Work Hours (8h/day)"
        ]);
      });
    });

    // Convert to CSV string
    const csvContent = rows.map(row => 
      row.map(cell => `"${cell}"`).join(",")
    ).join("\n");

    // Download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `jobs-analysis-${new Date().toISOString()}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatLastRefreshed = (date: string | null) => {
    if (!date) return "Never";
    const d = new Date(date);
    return d.toLocaleString();
  };

  if (initialLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Spinner size="lg" />
      </div>
    );
  }

  const headerActions = (
    <div className="flex gap-2 items-center">
      {/* Time Mode Toggle */}
      <Button
        onClick={() => setTimeMode(timeMode === "real" ? "work" : "real")}
        size="sm"
        variant={timeMode === "work" ? "default" : "secondary"}
        className="text-xs"
      >
        <Clock className="w-3 h-3 mr-1.5" />
        {timeMode === "real" ? "Real Time" : "Work Hours"}
      </Button>

      <Button
        onClick={exportToCSV}
        disabled={!data}
        size="sm"
        variant="secondary"
        className="text-xs"
      >
        <Download className="w-3.5 h-3.5 mr-1" />
        Export CSV
      </Button>
      <Button
        onClick={refreshJobs}
        disabled={refreshing}
        size="sm"
        className="text-xs"
      >
        <RefreshCw className={`w-3.5 h-3.5 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
        {refreshing ? 'Refreshing...' : 'Refresh'}
      </Button>
    </div>
  );

  return (
    <div className="flex flex-col h-full">
      <DetailHeader
        breadcrumb={[
          { label: "Reporting" }
        ]}
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        searchPlaceholder="Search spaces..."
        actions={headerActions}
      />

      <div className="flex-1 overflow-hidden min-h-0">
        <div className="flex h-full">
          {/* Left Pane - Stats & Explanations */}
          <div className="flex flex-col border-r border-border" style={{ width: '20%' }}>
            <div className="p-3 border-b border-border bg-muted/30">
              <h3 className="text-sm font-semibold">Overview</h3>
            </div>
            
            <div className="flex-1 overflow-auto p-3 flex flex-col">
              <div className="space-y-4">
                {/* Overall Stats */}
                <div className="space-y-2">
                  <h4 className="text-xs font-semibold text-foreground">Statistics</h4>
                  <div className="space-y-1.5 text-xs">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Total Spaces:</span>
                      <span className="font-medium">{data?.totalSpaces ?? "—"}</span>
                    </div>
                  </div>
                </div>

                {/* Average Initial Load Metrics */}
                <div className="space-y-2">
                  <h4 className="text-xs font-semibold text-foreground">Average Initial Load</h4>
                  <div className="space-y-1.5 text-xs">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Time in Mapping:</span>
                      <span className="font-medium">
                        {data?.averageMetrics ? formatDuration(getDisplayTime(data.averageMetrics.avgTimeInMapping, true)) : "—"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Files Mapped:</span>
                      <span className="font-medium">
                        {data?.averageMetrics?.avgNumberOfFilesMapped ?? "—"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Identity Resolution:</span>
                      <span className="font-medium">
                        {data?.averageMetrics ? formatDuration(getDisplayTime(data.averageMetrics.avgTimeInIdentityResolution)) : "—"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Validations:</span>
                      <span className="font-medium">
                        {data?.averageMetrics ? formatDuration(getDisplayTime(data.averageMetrics.avgTimeInValidations)) : "—"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Total Time:</span>
                      <span className="font-medium">
                        {data?.averageMetrics ? formatDuration(getDisplayTime(data.averageMetrics.avgTotalTime)) : "—"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Explanations - Bottom aligned */}
              <div className="mt-auto pt-4 space-y-3 border-t border-border">
                {/* Metrics Explanations */}
                <div className="space-y-2">
                  <h4 className="text-xs font-semibold text-muted-foreground">Timing Metrics</h4>
                  <div className="space-y-2 text-xs text-muted-foreground">
                    <div>
                      <p className="font-medium">Time in Mapping</p>
                      <p className="mt-0.5">
                        Sum of all map job durations (created → finished)
                      </p>
                    </div>
                    <div>
                      <p className="font-medium">Files Mapped</p>
                      <p className="mt-0.5">
                        Total count of map jobs executed
                      </p>
                    </div>
                    <div>
                      <p className="font-medium">Identity Resolution</p>
                      <p className="mt-0.5">
                        Time from generate job start to last federation job finish
                      </p>
                    </div>
                    <div>
                      <p className="font-medium">Validations</p>
                      <p className="mt-0.5">
                        Time from last federation finish to last export finish
                      </p>
                    </div>
                    <div>
                      <p className="font-medium">Total Time (*)</p>
                      <p className="mt-0.5">
                        Complete: first map to last export. Incomplete: first map to now
                      </p>
                    </div>
                  </div>
                </div>

                {/* Time Display Modes */}
                <div className="space-y-2">
                  <h4 className="text-xs font-semibold text-muted-foreground">Time Display</h4>
                  <div className="space-y-2 text-xs text-muted-foreground">
                    <div>
                      <p className="font-medium">Real Time</p>
                      <p className="mt-0.5">
                        Actual elapsed time including nights/weekends
                      </p>
                    </div>
                    <div>
                      <p className="font-medium">Work Hours</p>
                      <p className="mt-0.5">
                        Adjusted to 8-hour work days (≤8h unchanged, {'>'}8h converted). Mapping time never adjusted.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Cycle Types */}
                <div className="space-y-2">
                  <h4 className="text-xs font-semibold text-muted-foreground">Cycle Types</h4>
                  <div className="space-y-2 text-xs text-muted-foreground">
                    <div>
                      <p className="font-medium">Initial Load</p>
                      <p className="mt-0.5">
                        Jobs between configure and first update-project
                      </p>
                    </div>
                    <div>
                      <p className="font-medium">Dual Maintenance</p>
                      <p className="mt-0.5">
                        Jobs between consecutive update-project jobs
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Pane - Table */}
          <div className="flex-1 flex flex-col">
            <div className="p-3 border-b border-border bg-muted/30 flex justify-between items-center">
              <h3 className="text-sm font-semibold">Space Analysis</h3>
              <p className="text-xs text-muted-foreground">
                Last run: {formatLastRefreshed(data?.createdAt || null)}
              </p>
            </div>
            
            <div className="flex-1 overflow-auto p-3">
              {!data ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center max-w-md">
                    <p className="text-sm text-muted-foreground mb-3">
                      No analysis data available. Click refresh to run analysis.
                    </p>
                  </div>
                </div>
              ) : filteredSpaces.length === 0 ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center max-w-md">
                    <p className="text-sm text-muted-foreground mb-3">
                      No spaces found matching "{searchValue}"
                    </p>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredSpaces.map((space) => (
                    <div key={space.spaceId} className="bg-card border rounded-md overflow-hidden">
                      <div className="bg-muted px-2.5 py-1.5 border-b">
                        <h2 className="text-xs font-semibold">{space.spaceName}</h2>
                        <p className="text-xs text-muted-foreground">{space.spaceId}</p>
                      </div>
                      
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs table-fixed">
                          <colgroup>
                            <col style={{ width: '35%' }} />
                            <col style={{ width: '13%' }} />
                            <col style={{ width: '8%' }} />
                            <col style={{ width: '13%' }} />
                            <col style={{ width: '13%' }} />
                            <col style={{ width: '13%' }} />
                          </colgroup>
                          <thead>
                            <tr className="border-b bg-muted/50">
                              <th className="text-left p-2 font-medium">Cycle</th>
                              <th className="text-right p-2 font-medium">Mapping</th>
                              <th className="text-right p-2 font-medium">Files</th>
                              <th className="text-right p-2 font-medium">Identity</th>
                              <th className="text-right p-2 font-medium">Validation</th>
                              <th className="text-right p-2 font-medium">Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {space.initialLoad && (
                              <tr className="border-b hover:bg-muted/30">
                                <td className="p-2 font-medium truncate">
                                  {space.initialLoad.cycleName}
                                  {!space.initialLoad.isComplete && (
                                    <span className="ml-1.5 text-xs text-amber-600 dark:text-amber-500">
                                      (Not Exported)
                                    </span>
                                  )}
                                </td>
                                <td className="p-2 text-right">{formatDuration(getDisplayTime(space.initialLoad.timeInMapping, true))}</td>
                                <td className="p-2 text-right">{space.initialLoad.numberOfFilesMapped}</td>
                                <td className="p-2 text-right">{formatDuration(getDisplayTime(space.initialLoad.timeInIdentityResolution))}</td>
                                <td className="p-2 text-right">{formatDuration(getDisplayTime(space.initialLoad.timeInValidations))}</td>
                                <td className="p-2 text-right font-semibold">
                                  {formatDuration(getDisplayTime(space.initialLoad.totalTime))}
                                  {!space.initialLoad.isComplete && (
                                    <span className="ml-1 text-xs font-normal text-muted-foreground">*</span>
                                  )}
                                </td>
                              </tr>
                            )}
                            {space.dualMaintenanceCycles.map((cycle) => (
                              <tr key={`${space.spaceId}-${cycle.cycleName}`} className="border-b hover:bg-muted/30">
                                <td className="p-2 font-medium truncate">
                                  {cycle.cycleName}
                                  {!cycle.isComplete && (
                                    <span className="ml-1.5 text-xs text-amber-600 dark:text-amber-500">
                                      (Not Exported)
                                    </span>
                                  )}
                                </td>
                                <td className="p-2 text-right">{formatDuration(getDisplayTime(cycle.timeInMapping, true))}</td>
                                <td className="p-2 text-right">{cycle.numberOfFilesMapped}</td>
                                <td className="p-2 text-right">{formatDuration(getDisplayTime(cycle.timeInIdentityResolution))}</td>
                                <td className="p-2 text-right">{formatDuration(getDisplayTime(cycle.timeInValidations))}</td>
                                <td className="p-2 text-right font-semibold">
                                  {formatDuration(getDisplayTime(cycle.totalTime))}
                                  {!cycle.isComplete && (
                                    <span className="ml-1 text-xs font-normal text-muted-foreground">*</span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {!space.initialLoad && space.dualMaintenanceCycles.length === 0 && (
                        <div className="p-3 text-center text-xs text-muted-foreground">
                          No cycles found for this space
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default JobsPage;

